import React from "react";
import Homeroom from "../components/Homeroom";
import HouseholdGadget from "../components/HouseholdGadget";
import {
    BookOpen,
    CalendarDays,
    CheckSquare,
    ChevronDown,
    ChevronUp,
    FileText,
    LogOut,
} from "lucide-react";
import {
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { api } from "../lib/api";
import ChildNotesPanel from "@/components/ChildNotesPanel";

const dashboardCardClass = `
    group
    rounded-[32px]
    border
    bg-black
    p-6
    text-left
    transition-all
    duration-200
    hover:-translate-y-1
`;
const purpleActionBtn =
    "rounded-2xl border border-[#ffd166]/45 bg-black px-4 py-2 text-sm font-semibold text-[#ffd166] shadow-[0_0_16px_rgba(255,209,102,0.18)] transition-all duration-200 hover:border-[#ffd166] hover:bg-[#ffd166]/10 hover:shadow-[0_0_28px_rgba(255,209,102,0.35)] active:scale-[0.98]";

const ChildDashboard = () => {
    const navigate = useNavigate();
    const { childId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedDesk = ["chores", "behavior", "merits"].includes(searchParams.get("desk")) ? searchParams.get("desk") : "";
    const openWorkingDesk = (type) => {
        setSearchParams({ desk: type });
        setTimeout(() => {
            const desk = document.getElementById("working-desk-household");
            if (desk) { desk.open = true; desk.scrollIntoView?.({ behavior: "smooth", block: "start" }); }
        }, 0);
    };

    const [child, setChild] =
        React.useState(null);

    const [loading, setLoading] =
        React.useState(true);

    const [errorMessage, setErrorMessage] =
        React.useState("");

    const [childSubjects, setChildSubjects] =
    React.useState([]);

    const [
        classroomPickerOpen,
        setClassroomPickerOpen,
    ] = React.useState(false);

    React.useEffect(() => {
        const refreshMerits = async () => { try { const response = await api.get("/auth/child/me"); setChild(response.data); } catch { /* Session refresh retries when reopened. */ } };
        window.addEventListener("shadowGroveMeritsUpdated", refreshMerits);
        return () => window.removeEventListener("shadowGroveMeritsUpdated", refreshMerits);
    }, []);

    const [
        isNotesOpen,
        setIsNotesOpen,
    ] = React.useState(true);

    React.useEffect(() => {
        let cancelled = false;

        const loadChildSession = async () => {
            try {
                const [
                    childResponse,
                    subjectsResponse,
                ] = await Promise.all([
                    api.get("/auth/child/me"),
                    api.get("/auth/child/subjects"),
                ]);

                if (cancelled) return;

                const authenticatedChild =
                    childResponse.data;

                const authenticatedSubjects =
                    Array.isArray(
                        subjectsResponse.data
                )
                        ? subjectsResponse.data
                        : [];

                if (
                    !authenticatedChild?.child_id
                ) {
                    throw new Error(
                        "Child profile was not returned."
                    );
                }

                if (
                    String(
                        authenticatedChild.child_id
                    ) !== String(childId)
                ) {
                    navigate(
                        `/child/home/${authenticatedChild.child_id}`,
                        {
                            replace: true,
                        }
                    );

                    return;
                }

                setChild(
                    authenticatedChild
                );

                setChildSubjects(
                    authenticatedSubjects
                );
            } catch (error) {
                if (cancelled) return;

                setErrorMessage(
                    error?.response?.data?.detail ||
                        "Your child session has expired."
                );

                navigate(
                    "/child/login",
                    {
                        replace: true,
                    }
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadChildSession();

        return () => {
            cancelled = true;
        };
    }, [childId, navigate]);

    const handleLogout = async () => {
        try {
            await api.post(
                "/auth/child/logout"
            );
        } catch (error) {
            console.error(
                "Unable to clear child session:",
                error
            );
        }

        navigate(
            "/child/login",
            {
                replace: true,
            }
        );
    };
    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#05070d] text-[#fff3c4]">
                <p className="animate-pulse text-sm">
                    Opening the Atrium…
                </p>
            </div>
        );
    }

    if (!child) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#05070d] px-6 text-center text-white">
                <div>
                    <p className="text-red-200">
                        {errorMessage ||
                            "Child dashboard unavailable."}
                    </p>
                </div>
            </div>
        );
    }

    const childName =
        child.name?.trim() ||
        "Student";

const todayLabel = new Intl.DateTimeFormat(
    "en-US",
    {
        weekday: "long",
        month: "long",
        day: "numeric",
    }
).format(new Date());

    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-[#05070d]
                text-white
                bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.16),transparent_40%)]
            "
        >
{classroomPickerOpen && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4 backdrop-blur-sm">
        <div className="w-full max-w-lg overflow-hidden rounded-[32px] border border-[#ffd166]/45 bg-black shadow-[0_0_45px_rgba(255,209,102,0.20)]">
            <div className="border-b border-[#ffd166]/25 px-6 py-5 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b4fe]">
                    Aula
                </p>

                <h2 className="mt-2 font-display text-3xl text-[#ffd166]">
                    Choose a Class
                </h2>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    Select the classroom you wish to enter.
                </p>
            </div>

            <div className="max-h-[60vh] space-y-3 overflow-y-auto px-6 py-5">
                {childSubjects.map(
                    (currentSubject) => {
                        const currentSubjectId =
                            currentSubject.subject_id ||
                            currentSubject.id;

                        return (
                            <button
                                key={currentSubjectId}
                                type="button"
                                onClick={() => {
                                    setClassroomPickerOpen(
                                        false
                                    );

                                    navigate(
                                        `/child/classroom/${child.child_id}/${currentSubjectId}`
                                    );
                                }}
                                className="flex w-full items-center gap-4 rounded-2xl border bg-[#080b14] px-4 py-4 text-left transition hover:-translate-y-0.5 hover:bg-[#ffd166]/5"
                                style={{
                                    borderColor:
                                        currentSubject.color ||
                                        "#ffd166",
                                    boxShadow: `0 0 18px ${
                                        currentSubject.color ||
                                        "#ffd166"
                                    }33`,
                                }}
                            >
                                <span
                                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border bg-[#2b1548]/65"
                                    style={{
                                        borderColor:
                                            currentSubject.color ||
                                            "#ffd166",
                                        color:
                                            currentSubject.color ||
                                            "#ffd166",
                                    }}
                                >
                                    <BookOpen
                                        size={21}
                                    />
                                </span>

                                <span className="min-w-0">
                                    <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                                        Classroom
                                    </span>

                                    <span className="mt-1 block truncate font-display text-xl text-[#ffd166]">
                                        {
                                            currentSubject.name
                                        }
                                    </span>
                                </span>
                            </button>
                        );
                    }
                )}
            </div>

            <div className="flex justify-end border-t border-[#ffd166]/25 px-6 py-5">
                <button
                    type="button"
                    onClick={() =>
                        setClassroomPickerOpen(false)
                    }
                    className={purpleActionBtn}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
            <div className="absolute left-1/2 top-[-220px] h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#7c3aed]/10 blur-3xl" />

            <header className="relative z-10 border-b border-[#ffd166]/20 bg-black/70 backdrop-blur-sm">
                <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-5">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#d8b4fe]">
                            Liberi Curiosi
                        </p>

                        <h1 className="mt-1 font-display text-2xl text-[#ffd166]">
                            Atrium
                        </h1>
                    </div>

                    <button
                        type="button"
                        onClick={handleLogout}
                        className="
                            inline-flex
                            items-center
                            justify-center
                            gap-2
                            rounded-2xl
                            border
                            border-[#ffd166]/40
                            bg-[#2b1548]/70
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-[#fff3c4]
                            transition
                            hover:border-[#ffd166]
                            hover:text-[#ffd166]
                        "
                    >
                        <LogOut size={16} />
                        Sign Out
                    </button>
                </div>
            </header>

            <main className="relative z-10 mx-auto max-w-[1600px] px-6 py-10">
                <div
                    className={`grid items-start gap-6 ${
                        isNotesOpen
                            ? "md:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
                            : "md:grid-cols-[minmax(0,1fr)_auto]"
                    }`}
                >
                    <div className="min-w-0">
                <section
                    className="
                        rounded-[32px]
                        border
                        border-[#ffd166]/45
                        bg-black
                        p-7
                        shadow-[0_0_40px_rgba(255,209,102,0.10)]
                    "
                >
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#d8b4fe]">
                        Atrium Discipuli
                    </p>

                    <h2 className="mt-2 font-display text-4xl text-[#ffd166]">
                        Welcome, {childName}
                    </h2>

                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#b0b8d6]">
                        Enter your classroom, complete your daily duties, build Merita through
                        learning and effort, and earn Denarii from approved quests and chores.
                    </p>
                </section>

<nav
    aria-label="Child dashboard destinations"
    className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
>
    <button type="button" aria-pressed={!requestedDesk} className={`${dashboardCardClass} !rounded-2xl !p-4 text-[#ffd166]`} onClick={() => setSearchParams({})}><span className="font-display text-lg">Homeroom</span><span className="mt-1 block text-xs text-[#b0b8d6]">Selected gadgets, messages and calendar</span></button>
<button
    type="button"
    onClick={() => {
        if (childSubjects.length === 0) {
            return;
        }

        if (childSubjects.length === 1) {
            const onlySubject =
                childSubjects[0];

            const onlySubjectId =
                onlySubject.subject_id ||
                onlySubject.id;

            navigate(
                `/child/classroom/${child.child_id}/${onlySubjectId}`
            );

            return;
        }

        setClassroomPickerOpen(true);
    }}
    disabled={childSubjects.length === 0}
    className={`${dashboardCardClass} !rounded-2xl !p-4 disabled:cursor-not-allowed disabled:opacity-65`}
    style={{
        borderColor:
            childSubjects.length === 1
                ? childSubjects[0]?.color ||
                  "#ffd166"
                : "#ffd166",
        boxShadow:
            "0 0 22px rgba(255,209,102,0.12)",
    }}
>
    <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ffd166]/45 bg-[#2b1548]/65 text-[#ffd166]">
            <BookOpen size={21} />
        </div>

        <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                Classroom
            </p>

            <h3 className="mt-1 font-display text-lg text-[#ffd166]">
                {childSubjects.length === 0
                    ? "No Classes Yet"
                    : childSubjects.length === 1
                    ? childSubjects[0].name
                    : "Choose a Class"}
            </h3>

            {childSubjects.length > 1 && (
                <p className="mt-1 text-xs text-[#b0b8d6]">
                    {childSubjects.length} classes available
                </p>
            )}

            {childSubjects.length === 0 && (
                <p className="mt-1 text-xs text-[#b0b8d6]">
                    A parent must add a subject first.
                </p>
            )}
        </div>
    </div>
</button>    

    <button
        type="button"
        onClick={() => openWorkingDesk("chores")}
        className={`${dashboardCardClass} !rounded-2xl !p-4`}
        style={{
            borderColor:
                "rgba(216,180,254,0.65)",
            boxShadow:
                "0 0 22px rgba(168,85,247,0.11)",
        }}
    >
        <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#d8b4fe]/45 bg-[#2b1548]/65 text-[#d8b4fe]">
                <CheckSquare size={21} />
            </div>

            <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                    Household Duties
                </p>

                <h3 className="mt-1 font-display text-lg text-[#ffd166]">
                    Munera Domus
                </h3>
            </div>
        </div>
    </button>

    {child.show_academic_record && (
        <button
            type="button"
            disabled
            className={`${dashboardCardClass} !cursor-not-allowed !rounded-2xl !p-4 opacity-75`}
            style={{
                borderColor:
                    "rgba(255,209,102,0.35)",
            }}
        >
            <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ffd166]/35 bg-[#2b1548]/50 text-[#ffd166]">
                    <FileText size={21} />
                </div>

                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                        Coming Soon
                    </p>

                    <h3 className="mt-1 font-display text-lg text-[#ffd166]">
                        Academic Record
                    </h3>
                </div>
            </div>
        </button>
    )}
</nav>

<section className="mt-6 rounded-[32px] border border-[#ffd166]/35 bg-black p-6 shadow-[0_0_28px_rgba(255,209,102,0.10)]">
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#ffd166]/20 pb-4">
        <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b4fe]">
                {requestedDesk === "chores" ? "Today · Household Duties" : "Homeroom"}
            </p>

            <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
                {todayLabel}
            </h3>
        </div>

        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#ffd166]/40 bg-[#2b1548]/60 text-[#ffd166]">
            <CalendarDays size={23} />
        </div>
    </div>

    <div className="mt-5">
        {requestedDesk ? <details open id="working-desk-household"><summary className="mb-4 cursor-pointer font-display text-2xl text-[#ffd166]">{requestedDesk === "chores" ? "Household Duties · Munera Domus" : requestedDesk === "behavior" ? "Behavior" : "Merits"}</summary><HouseholdGadget key={requestedDesk} type={requestedDesk} role="child" children={[child]} /></details> : <Homeroom key={child.child_id} role="child" userId={child.child_id} children={[child]} subjects={childSubjects} />}
    </div>
</section>
        </div>

        <ChildNotesPanel
            childId={child.child_id}
            scope="dashboard"
            title="Atrium Notes"
            onOpenChange={setIsNotesOpen}
        />
    </div>
</main>
        </div>
    );
};

export default ChildDashboard;
